import React, { createContext, useContext, useState, useCallback } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import { ApiError } from '@yawi/infrastructure';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (toast: Omit<Toast, 'id'>) => void;
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  warning: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

const FIELD_LABELS: Record<string, string> = {
  depositAmount: 'El monto del deposito',
};

const translateBackendValidationMessage = (message: string): string => {
  const minMatch = message.match(/^(\w+) must not be less than (\d+)$/);
  if (minMatch) {
    const fieldLabel = FIELD_LABELS[minMatch[1]] ?? 'El valor';
    return `${fieldLabel} no puede ser menor a ${minMatch[2]}`;
  }

  return message;
};

const translateBackendValidationMessages = (messages: string[]): string => (
  messages.map(translateBackendValidationMessage).join('. ')
);

// Helper para extraer mensaje de error de diferentes fuentes
export const getErrorMessage = (error: unknown): string => {
  if (ApiError.isApiError(error)) {
    if (error.statusCode === 409) {
      return formatConflictMessage(error);
    }
    return translateBackendValidationMessage(error.message);
  }
  if (error instanceof Error) {
    return translateBackendValidationMessage(error.message);
  }
  if (typeof error === 'string') {
    return error;
  }
  if (typeof error === 'object' && error !== null) {
    // Manejar estructura de error del backend { message: string | string[] }
    const err = error as Record<string, unknown>;
    if ('message' in err) {
      if (Array.isArray(err.message)) {
        return translateBackendValidationMessages(err.message.filter((message): message is string => typeof message === 'string'));
      }
      if (typeof err.message === 'string') {
        return translateBackendValidationMessage(err.message);
      }
    }
  }
  return 'Ha ocurrido un error inesperado';
};

// El 409 MEMBER_HAS_DEPENDENTS manda { dependents: { rfid, vehicles, plates, pets,
// pendingCharges, activeReservations, total } } — un desglose por categoría con
// conteos, no una lista de códigos.
const DEPENDENT_COUNT_LABELS: Record<string, { singular: string; plural: string }> = {
  rfid: { singular: 'tarjeta RFID', plural: 'tarjetas RFID' },
  vehicles: { singular: 'vehículo', plural: 'vehículos' },
  plates: { singular: 'placa', plural: 'placas' },
  pets: { singular: 'mascota', plural: 'mascotas' },
  pendingCharges: { singular: 'cargo pendiente', plural: 'cargos pendientes' },
  activeReservations: { singular: 'reserva de amenidad activa', plural: 'reservas de amenidad activas' },
};

// El 409 SECT_1004 manda { relations: { units, accessDoors, announcements, polls,
// documents, transparencyProjects, amenities, members } }. `members` son las
// filas de `member_sections`: el administrador de la sección o el personal
// acotado a ella.
const SECTION_RELATION_LABELS: Record<string, { singular: string; plural: string }> = {
  units: { singular: 'unidad', plural: 'unidades' },
  accessDoors: { singular: 'puerta de acceso', plural: 'puertas de acceso' },
  announcements: { singular: 'comunicado', plural: 'comunicados' },
  polls: { singular: 'encuesta', plural: 'encuestas' },
  documents: { singular: 'documento', plural: 'documentos' },
  transparencyProjects: { singular: 'proyecto de transparencia', plural: 'proyectos de transparencia' },
  amenities: { singular: 'amenidad', plural: 'amenidades' },
  members: { singular: 'miembro asignado', plural: 'miembros asignados' },
};

/**
 * Convierte un `{ clave: conteo }` del backend en "3 unidades, 1 comunicado".
 * Devuelve null si no hay nada que enumerar, para que el caller caiga a su
 * mensaje genérico.
 */
function formatCountBreakdown(
  raw: unknown,
  labels: Record<string, { singular: string; plural: string }>,
): string | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const parts = Object.entries(raw as Record<string, unknown>)
    .filter(([key, count]) => key !== 'total' && typeof count === 'number' && count > 0)
    .map(([key, count]) => {
      const label = labels[key];
      const noun = label ? ((count as number) === 1 ? label.singular : label.plural) : key;
      return `${count as number} ${noun}`;
    });
  return parts.length > 0 ? parts.join(', ') : null;
}

function formatConflictMessage(error: ApiError): string {
  if (error.code === 'MEMBER_HAS_DEPENDENTS') {
    const parts = formatCountBreakdown(error.details?.dependents, DEPENDENT_COUNT_LABELS);
    if (parts) {
      return `No se puede eliminar: tiene ${parts}. Elimina estos registros primero.`;
    }
    return 'No se puede eliminar porque el usuario tiene registros dependientes. Elimina los datos relacionados primero.';
  }
  // SECT_1004: el backend manda `details.relations` con el conteo por tabla. Sin
  // enumerarlo el mensaje es inútil: seis de las ocho tablas no se ven en la
  // pantalla de secciones, así que el admin lee "tiene información
  // relacionada", ve la fila con 0 unidades y 0 residentes, y no sabe qué tocar.
  if (error.code === 'SECT_1004') {
    const parts = formatCountBreakdown(error.details?.relations, SECTION_RELATION_LABELS);
    if (parts) {
      return `No se puede eliminar la sección: tiene ${parts}. Libera estos registros primero.`;
    }
  }
  // El resto de los 409 (nombre de unidad duplicado, reglas de residente
  // principal, etc.) ya traen un mensaje claro en español del backend — se
  // muestra tal cual en vez de un texto genérico que no aplica al caso real.
  if (typeof error.message === 'string' && error.message.trim()) {
    return error.message;
  }
  return 'No se puede completar la operación porque el registro tiene datos asociados. Elimina los datos relacionados primero.';
}

const toastConfig: Record<ToastType, { icon: React.ReactNode; bgColor: string; borderColor: string; iconColor: string }> = {
  success: {
    icon: <CheckCircle size={20} />,
    bgColor: 'bg-emerald-50 dark:bg-emerald-900',
    borderColor: 'border-emerald-200 dark:border-emerald-800',
    iconColor: 'text-emerald-500',
  },
  error: {
    icon: <AlertCircle size={20} />,
    bgColor: 'bg-red-50 dark:bg-red-900',
    borderColor: 'border-red-200 dark:border-red-800',
    iconColor: 'text-red-500',
  },
  warning: {
    icon: <AlertTriangle size={20} />,
    bgColor: 'bg-amber-50 dark:bg-amber-900',
    borderColor: 'border-amber-200 dark:border-amber-800',
    iconColor: 'text-amber-500',
  },
  info: {
    icon: <Info size={20} />,
    bgColor: 'bg-blue-50 dark:bg-blue-900',
    borderColor: 'border-blue-200 dark:border-blue-800',
    iconColor: 'text-blue-500',
  },
};

const ToastItem: React.FC<{ toast: Toast; onDismiss: () => void }> = ({ toast, onDismiss }) => {
  const config = toastConfig[toast.type];

  React.useEffect(() => {
    if (toast.duration !== 0) {
      const timer = setTimeout(onDismiss, toast.duration || 5000);
      return () => clearTimeout(timer);
    }
  }, [toast.duration, onDismiss]);

  return (
    <div
      className={`
        flex items-start gap-3 p-4 rounded-xl border shadow-lg
        ${config.bgColor} ${config.borderColor}
        animate-in slide-in-from-right-5 fade-in duration-300
        min-w-[320px] max-w-[420px]
      `}
      role="alert"
    >
      <span className={`flex-shrink-0 ${config.iconColor}`}>{config.icon}</span>
      <div className="flex-1 min-w-0">
        {toast.title && (
          <h4 className="font-semibold text-slate-900 dark:text-white text-sm mb-0.5">
            {toast.title}
          </h4>
        )}
        <p className="text-sm text-slate-600 dark:text-slate-300 break-words">
          {toast.message}
        </p>
      </div>
      <button
        onClick={onDismiss}
        className="flex-shrink-0 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const success = useCallback((message: string, title?: string) => {
    showToast({ type: 'success', message, title });
  }, [showToast]);

  const error = useCallback((message: string, title?: string) => {
    showToast({ type: 'error', message, title, duration: 8000 }); // Errores duran más
  }, [showToast]);

  const warning = useCallback((message: string, title?: string) => {
    showToast({ type: 'warning', message, title });
  }, [showToast]);

  const info = useCallback((message: string, title?: string) => {
    showToast({ type: 'info', message, title });
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ toasts, showToast, success, error, warning, info, dismiss }}>
      {children}
      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={() => dismiss(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
